// App.jsx
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ScrollToTop from "./components/ScrollToTop"; 
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ThemeToggle from './components/ThemeToggle';
import { ThemeProvider } from './contexts/ThemeContext';
import Home from './pages/Home';
import Projects from './pages/Projects';
import Contact from './pages/Contact';
import TimelinePage from './pages/TimelinePage';
import Resume from './pages/Resume';
import Analytics from './pages/Analytics';
import NotFound from "./pages/NotFound";
import { initGA, trackPageView } from './utils/analytics';
import ThankYou from "./pages/ThankYou";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react"


// ✅ Tracker Component
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);

    if (window.plausible) {
      window.plausible("pageview", {
        props: {
          path: location.pathname,
        },
      });
    }
  }, [location.pathname, location.search]);

  return null;
};

// ✅ Route Wrapper with Animation
const AnimatedRoutes = () => {
  const location = useLocation();

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 },
  };

  const pageTransition = {
    duration: 0.4,
    ease: "easeInOut",
  };

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <Home />
          </motion.div>
        } />
        <Route path="/projects" element={
          <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <Projects />
          </motion.div>
        } />
        <Route path="/timeline" element={
          <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <TimelinePage />
          </motion.div>
        } />
        <Route path="/resume" element={
          <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <Resume />
          </motion.div>
        } />
        <Route path="/contact" element={
          <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <Contact />
          </motion.div>
        } />
        <Route path="/thank-you" element={
          <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <ThankYou />
          </motion.div>
        } />
        <Route path="/analytics" element={
          <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <Analytics />
          </motion.div>
        } />
        <Route path="*" element={
          <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <NotFound />
          </motion.div>
        } />
      </Routes>
    </AnimatePresence>
  );
};

// ✅ Main App
function App() {
  return (
    <ThemeProvider>
      <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 min-h-screen text-gray-800 dark:text-gray-100 font-sans transition-colors duration-300">
        <Router>
          <AnalyticsTracker />
          <VercelAnalytics />
          <ScrollToTop />
          <ThemeToggle />
          <Navbar />
          <div className="pt-20">
            <AnimatedRoutes />
          </div>
          <Footer />
        </Router>
      </div>
    </ThemeProvider>
  );
}

export default App;
